import type { Vec2 } from '@learnforge/demo-math/geometry';
import { formatTick, niceTickStep } from '@learnforge/demo-math/numeric';
import type { Projector } from './curve';
import {
  boundaryTextAnchor,
  createSvgEl,
  getCartesianAxisLayout,
  type CartesianDomain,
} from '@learnforge/demo-render';

export interface DualPaneOptions {
  /** Shared world x-domain for both panes. */
  xDomain: [number, number];
  /** World y-domain of the top pane (e.g. f). */
  topYDomain: [number, number];
  /** World y-domain of the bottom pane (e.g. f′ or A). */
  bottomYDomain: [number, number];
  width: number; // 800
  height: number; // 460
  gap: number; // px between panes
}

export interface DualPaneHandle {
  top: DualPaneProjector; // worldToScreen into the upper rect
  bottom: DualPaneProjector; // worldToScreen into the lower rect
  /** Clipped upper-pane layer for curves, fills, and other pane-local content. */
  readonly topContent: SVGGElement;
  /** Clipped lower-pane layer for curves, fills, and other pane-local content. */
  readonly bottomContent: SVGGElement;
  /** Draws axis lines + x/y ticks for both panes; call once in setup. */
  drawAxes(): void;
  /**
   * Update the domains used by both projectors in-place.  The existing
   * `top`/`bottom` Projector objects are RETAINED (curve layers hold
   * references to them) — only the internal mapping is updated.
   * If axes have already been drawn, they are cleared and redrawn for the
   * new domains so repeated calls never accumulate stale elements.
   */
  setDomains(d: {
    xDomain: [number, number];
    topYDomain: [number, number];
    bottomYDomain: [number, number];
  }): void;
  destroy(): void;
}

export interface DualPaneProjector extends Projector {
  screenToWorld(point: Vec2): Vec2;
  getDomains(): CartesianDomain;
}

/** Target count shared with the mutable Cartesian plane. */
const TARGET_TICKS = 10;
/** Half-length of a tick mark in screen pixels. */
const TICK_HALF = 4;
/** Font size for tick labels. */
const TICK_FONT = 10;
let clipId = 0;

/**
 * Mutable domain state shared between a projector closure and the handle's
 * setDomains implementation.  Fields are mutated in place so the projector
 * object reference stays stable (curve layers hold it directly).
 */
interface MutableDomain {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

function makeProjectorMutable(
  domain: MutableDomain,
  screenTop: number,
  screenBottom: number,
  width: number,
): DualPaneProjector {
  return {
    worldToScreen(p: Vec2): Vec2 {
      // x: linear map from xDomain → [0, width]
      const sx = ((p.x - domain.xMin) / (domain.xMax - domain.xMin)) * width;
      // y: linear map from yDomain → [screenTop, screenBottom], inverted
      // larger world-y → smaller screen-y (screen top = world max)
      const sy =
        screenBottom -
        ((p.y - domain.yMin) / (domain.yMax - domain.yMin)) * (screenBottom - screenTop);
      return { x: sx, y: sy };
    },
    screenToWorld(p: Vec2): Vec2 {
      return {
        x: domain.xMin + (p.x / width) * (domain.xMax - domain.xMin),
        y:
          domain.yMin +
          ((screenBottom - p.y) / (screenBottom - screenTop)) * (domain.yMax - domain.yMin),
      };
    },
    getDomains(): CartesianDomain {
      return {
        xDomain: [domain.xMin, domain.xMax],
        yDomain: [domain.yMin, domain.yMax],
      };
    },
  };
}

function drawPaneAxes(
  group: SVGGElement,
  projector: Projector,
  xDomain: [number, number],
  yDomain: [number, number],
): void {
  const [xMin, xMax] = xDomain;
  const [yMin, yMax] = yDomain;

  const axisLayout = getCartesianAxisLayout(xDomain, yDomain);

  // X-axis line (world y = 0, clamped to the nearest pane edge)
  const xAxisY = axisLayout.xAxis.value;
  const xLeft = projector.worldToScreen({ x: xMin, y: xAxisY });
  const xRight = projector.worldToScreen({ x: xMax, y: xAxisY });
  const xAxisLine = createSvgEl('line');
  xAxisLine.setAttribute('x1', String(xLeft.x));
  xAxisLine.setAttribute('y1', String(xLeft.y));
  xAxisLine.setAttribute('x2', String(xRight.x));
  xAxisLine.setAttribute('y2', String(xRight.y));
  xAxisLine.setAttribute('class', 'dual-pane-axis');
  group.appendChild(xAxisLine);

  // Y-axis line (world x = 0, clamped to the nearest pane edge)
  const yAxisX = axisLayout.yAxis.value;
  const yBottom = projector.worldToScreen({ x: yAxisX, y: yMin });
  const yTop = projector.worldToScreen({ x: yAxisX, y: yMax });
  const yAxisLine = createSvgEl('line');
  yAxisLine.setAttribute('x1', String(yBottom.x));
  yAxisLine.setAttribute('y1', String(yBottom.y));
  yAxisLine.setAttribute('x2', String(yTop.x));
  yAxisLine.setAttribute('y2', String(yTop.y));
  yAxisLine.setAttribute('class', 'dual-pane-axis');
  group.appendChild(yAxisLine);

  // X-axis ticks use the same 1/2/5 × 10ⁿ step selection as Cartesian planes.
  const xStep = niceTickStep(xMax - xMin, TARGET_TICKS);
  const xEps = xStep * 1e-9;
  for (let wx = Math.ceil(xMin / xStep) * xStep; wx <= xMax + xEps; wx += xStep) {
    if (Math.abs(wx) < xEps) continue; // skip label at origin
    const sp = projector.worldToScreen({ x: wx, y: xAxisY });
    const tick = createSvgEl('line');
    tick.setAttribute('x1', String(sp.x));
    tick.setAttribute('y1', String(sp.y + axisLayout.xAxis.tickOffsets[0] * TICK_HALF));
    tick.setAttribute('x2', String(sp.x));
    tick.setAttribute('y2', String(sp.y + axisLayout.xAxis.tickOffsets[1] * TICK_HALF));
    tick.setAttribute('class', 'dual-pane-tick');
    group.appendChild(tick);

    const label = createSvgEl('text');
    label.setAttribute('x', String(sp.x));
    label.setAttribute(
      'y',
      String(sp.y + axisLayout.xAxis.labelDirection * (TICK_HALF + TICK_FONT)),
    );
    label.setAttribute('text-anchor', boundaryTextAnchor(sp.x, xLeft.x, xRight.x));
    label.setAttribute('data-axis', 'x');
    label.setAttribute('font-size', String(TICK_FONT));
    label.setAttribute('class', 'dual-pane-tick-label');
    label.textContent = formatTick(wx, xStep);
    group.appendChild(label);
  }

  // Y-axis ticks use the same 1/2/5 × 10ⁿ step selection as Cartesian planes.
  const yStep = niceTickStep(yMax - yMin, TARGET_TICKS);
  const yEps = yStep * 1e-9;
  for (let wy = Math.ceil(yMin / yStep) * yStep; wy <= yMax + yEps; wy += yStep) {
    if (Math.abs(wy) < yEps) continue; // skip label at origin
    const sp = projector.worldToScreen({ x: yAxisX, y: wy });
    const tick = createSvgEl('line');
    tick.setAttribute('x1', String(sp.x + axisLayout.yAxis.tickOffsets[0] * TICK_HALF));
    tick.setAttribute('y1', String(sp.y));
    tick.setAttribute('x2', String(sp.x + axisLayout.yAxis.tickOffsets[1] * TICK_HALF));
    tick.setAttribute('y2', String(sp.y));
    tick.setAttribute('class', 'dual-pane-tick');
    group.appendChild(tick);

    const label = createSvgEl('text');
    label.setAttribute('x', String(sp.x + axisLayout.yAxis.labelDirection * (TICK_HALF + 2)));
    label.setAttribute('y', String(sp.y + TICK_FONT / 2));
    label.setAttribute('text-anchor', axisLayout.yAxis.labelAnchor);
    label.setAttribute('data-axis', 'y');
    label.setAttribute('font-size', String(TICK_FONT));
    label.setAttribute('class', 'dual-pane-tick-label');
    label.textContent = formatTick(wy, yStep);
    group.appendChild(label);
  }
}

export function createDualPane(svg: SVGSVGElement, opts: DualPaneOptions): DualPaneHandle {
  const { xDomain, topYDomain, bottomYDomain, width, height, gap } = opts;

  // Fixed screen bands — these never change, only the world domains update.
  const topScreenTop = 0;
  const topScreenBottom = (height - gap) / 2;
  const bottomScreenTop = (height + gap) / 2;
  const bottomScreenBottom = height;

  const root = createSvgEl('g', { class: 'dual-pane' });
  const defs = createSvgEl('defs');
  const id = clipId++;
  const topClipId = `dual-pane-clip-top-${id}`;
  const bottomClipId = `dual-pane-clip-bottom-${id}`;
  const topClip = createSvgEl('clipPath', { id: topClipId, class: 'dual-pane-clip-top' });
  topClip.appendChild(
    createSvgEl('rect', {
      x: 0,
      y: topScreenTop,
      width,
      height: topScreenBottom - topScreenTop,
    }),
  );
  const bottomClip = createSvgEl('clipPath', {
    id: bottomClipId,
    class: 'dual-pane-clip-bottom',
  });
  bottomClip.appendChild(
    createSvgEl('rect', {
      x: 0,
      y: bottomScreenTop,
      width,
      height: bottomScreenBottom - bottomScreenTop,
    }),
  );
  defs.append(topClip, bottomClip);

  const topContent = createSvgEl('g', {
    class: 'dual-pane-content dual-pane-content-top',
    'clip-path': `url(#${topClipId})`,
  });
  const bottomContent = createSvgEl('g', {
    class: 'dual-pane-content dual-pane-content-bottom',
    'clip-path': `url(#${bottomClipId})`,
  });
  root.append(defs, topContent, bottomContent);
  svg.appendChild(root);

  // Mutable domain state — mutated by setDomains; read by the projector closures.
  const topDomain: MutableDomain = {
    xMin: xDomain[0],
    xMax: xDomain[1],
    yMin: topYDomain[0],
    yMax: topYDomain[1],
  };
  const bottomDomain: MutableDomain = {
    xMin: xDomain[0],
    xMax: xDomain[1],
    yMin: bottomYDomain[0],
    yMax: bottomYDomain[1],
  };

  // Stable projector objects — curve layers hold references to these.
  const topProjector = makeProjectorMutable(topDomain, topScreenTop, topScreenBottom, width);
  const bottomProjector = makeProjectorMutable(
    bottomDomain,
    bottomScreenTop,
    bottomScreenBottom,
    width,
  );

  /**
   * Axis <g> groups owned by the most recent drawAxes() call.
   * `setDomains` clears and replaces these; `destroy` removes whatever remains.
   */
  const axisGroups: SVGGElement[] = [];
  function clearAxisGroups(): void {
    for (const g of axisGroups) {
      g.remove();
    }
    axisGroups.length = 0;
  }

  function doDrawAxes(): void {
    const topGroup = createSvgEl('g');
    topGroup.setAttribute('class', 'dual-pane-axes dual-pane-axes-top');
    topContent.appendChild(topGroup);
    axisGroups.push(topGroup);
    drawPaneAxes(
      topGroup,
      topProjector,
      [topDomain.xMin, topDomain.xMax],
      [topDomain.yMin, topDomain.yMax],
    );

    const bottomGroup = createSvgEl('g');
    bottomGroup.setAttribute('class', 'dual-pane-axes dual-pane-axes-bottom');
    bottomContent.appendChild(bottomGroup);
    axisGroups.push(bottomGroup);
    drawPaneAxes(
      bottomGroup,
      bottomProjector,
      [bottomDomain.xMin, bottomDomain.xMax],
      [bottomDomain.yMin, bottomDomain.yMax],
    );
  }

  /** Whether drawAxes has been called at least once. */
  let axesDrawn = false;

  return {
    top: topProjector,
    bottom: bottomProjector,
    topContent,
    bottomContent,

    drawAxes(): void {
      // Idempotent: clear any previously-drawn axes before redrawing.
      clearAxisGroups();
      doDrawAxes();
      axesDrawn = true;
    },

    setDomains(d: {
      xDomain: [number, number];
      topYDomain: [number, number];
      bottomYDomain: [number, number];
    }): void {
      // Mutate in-place so existing projector references stay valid.
      topDomain.xMin = d.xDomain[0];
      topDomain.xMax = d.xDomain[1];
      topDomain.yMin = d.topYDomain[0];
      topDomain.yMax = d.topYDomain[1];

      bottomDomain.xMin = d.xDomain[0];
      bottomDomain.xMax = d.xDomain[1];
      bottomDomain.yMin = d.bottomYDomain[0];
      bottomDomain.yMax = d.bottomYDomain[1];

      // If axes were drawn, redraw them for the new domains.
      if (axesDrawn) {
        clearAxisGroups();
        doDrawAxes();
      }
    },

    destroy(): void {
      clearAxisGroups();
      root.remove();
      axesDrawn = false;
    },
  };
}
